/** Base interface for all PicoX exceptions */
export interface PicoXException extends Error {
  code: string;
}

/** Error thrown when there is a client-side issue in a network call */
export interface PicoXClientError extends PicoXException {
  code: 'PicoXClientError';
}

/** Error thrown when there is a server-side issue in a network call */
export interface PicoXServerError extends PicoXException {
  code: 'PicoXServerError';
}

/** Error thrown when there is a serialization issue */
export interface PicoXSerializationError extends PicoXException {
  code: 'PicoXSerializationError';
}

/**
 * Creates a new PicoXClientError
 *
 * @param message A generic message describing the error
 * @returns PicoXClientError instance
 */
export function PicoXClientError(message: string) {
  const error = new Error(message) as PicoXClientError;
  error.code = 'PicoXClientError';
  return error;
}

/**
 * Creates a new PicoXClientError
 *
 * @param message A generic message describing the error
 * @returns PicoXClientError instance
 */
export function PicoXSerializationError(message: string) {
  const error = new Error(message) as PicoXSerializationError;
  error.code = 'PicoXSerializationError';
  return error;
}

/**
 * Creates a new PicoXServerError
 *
 * @param message A generic message describing the error
 * @returns PicoXServerError instance
 */
export function PicoXServerError(message: string) {
  const error = new Error(message) as PicoXServerError;
  error.code = 'PicoXServerError';
  return error;
}

/**
 * Type guard to check if an error is a PicoXException
 *
 * @param error Error to check
 * @returns True if error is a PicoXException
 */
export function isPicoXException(error: unknown): error is PicoXException {
  return error instanceof Error && 'code' in error;
}

/**
 * Type guard to check if an error is a PicoXClientError
 *
 * @param error Error to check
 * @returns True if error is a PicoXClientError
 */
export function isPicoXClientError(error: unknown): error is PicoXClientError {
  return isPicoXException(error) && error.code === 'PicoXClientError';
}

/**
 * Type guard to check if an error is a PicoXServerError
 *
 * @param error Error to check
 * @returns True if error is a PicoXServerError
 */
export function isPicoXServerError(error: unknown): error is PicoXServerError {
  return isPicoXException(error) && error.code === 'PicoXServerError';
}

/**
 * Type guard to check if an error is a PicoXSerializationError
 *
 * @param error Error to check
 * @returns True if error is a PicoXSerializationError
 */
export function isPicoXSerializationError(error: unknown): error is PicoXSerializationError {
  return isPicoXException(error) && error.code === 'PicoXSerializationError';
}
